import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Heading, Text, VStack, HStack, Flex, Badge, Spinner, Button, Icon, useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaClone, FaSyncAlt, FaExchangeAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import MergeSubmissionsModal from './MergeSubmissionsModal';

// Same fingerprint the ingestion echo-detector uses: DOB + last name + ordered tests.
const testSetOf = (sub) => {
  let names = [];
  if (Array.isArray(sub.tests_on_the_script)) {
    names = sub.tests_on_the_script.map((t) => (t?.name || '').trim().toLowerCase()).filter(Boolean);
  }
  if (names.length === 0 && typeof sub.special_instructions === 'string') {
    const m = sub.special_instructions.match(/tests?:\s*(.+)/i);
    if (m) names = m[1].split(/[,;\n]/).map((s) => s.trim().toLowerCase()).filter(Boolean);
  }
  return [...new Set(names)].sort();
};
const lastNameOf = (sub) => (sub.patient_name || '').trim().toLowerCase().split(/\s+/).pop();
const fingerprint = (sub) => {
  const dob = sub.patient_dob;
  const last = lastNameOf(sub);
  if (!dob || !last) return null;
  return `${dob}|${last}|${testSetOf(sub).join(',')}`;
};
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString(); } catch { return d || '—'; } };

export default function AdminDuplicates() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pair, setPair] = useState(null); // { primary, duplicate } full rows for the modal

  const fetchRows = async () => {
    setLoading(true);
    const { data, error } = await selectAll(() => supabase
      .from('submissions')
      .select('id, patient_name, patient_dob, status, submitted_at, ldx_message_id, origin_ref, tests_on_the_script, special_instructions, merged_into')
      .is('merged_into', null)
      .neq('status', 'cancelled')
      .order('id', { ascending: true }));
    if (error) {
      toast({ title: 'Could not load submissions', description: error.message, status: 'error', duration: 5000 });
    } else {
      setRows(data || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchRows(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Group by fingerprint; keep only groups with 2+ members (candidate duplicates).
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const fp = fingerprint(r);
      if (!fp) continue;
      if (!map.has(fp)) map.set(fp, []);
      map.get(fp).push(r);
    }
    return [...map.values()].filter((g) => g.length > 1).sort((a, b) => b.length - a.length);
  }, [rows]);

  // Open the merge modal for two submissions (fetch full rows first).
  const openMerge = async (primaryLite, dupLite) => {
    const ids = [primaryLite.id, dupLite.id];
    const { data, error } = await supabase.from('submissions').select('*').in('id', ids);
    if (error || !data || data.length < 2) {
      toast({ title: 'Could not load submissions to merge', description: error?.message, status: 'error', duration: 4000 });
      return;
    }
    const primary = data.find((d) => d.id === primaryLite.id);
    const duplicate = data.find((d) => d.id === dupLite.id);
    setPair({ primary, duplicate });
  };

  return (
    <Container maxW="container.lg" py={6}>
      <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
        <HStack spacing={3}>
          <Icon as={FaClone} color="purple.500" boxSize={6} />
          <Box>
            <Heading size="lg">Possible Duplicates</Heading>
            <Text fontSize="sm" color="gray.500">{groups.length} group{groups.length === 1 ? '' : 's'} matched by patient DOB + name + tests</Text>
          </Box>
        </HStack>
        <Button size="sm" variant="outline" leftIcon={<FaSyncAlt />} onClick={fetchRows}>Refresh</Button>
      </Flex>

      {loading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <Box p={8} bg="white" borderRadius="lg" borderWidth="1px" textAlign="center">
          <Text color="gray.500">No likely duplicates found. 🎉</Text>
        </Box>
      ) : (
        <VStack align="stretch" spacing={4}>
          {groups.map((g) => {
            // Default primary = earliest id (most likely the original order).
            const primary = g[0];
            return (
              <Box key={fingerprint(primary)} bg="white" borderRadius="lg" borderWidth="1px" p={4}>
                <Text fontWeight="700" mb={1}>{primary.patient_name} · DOB {fmtDate(primary.patient_dob)}</Text>
                <Text fontSize="xs" color="gray.500" mb={3}>{testSetOf(primary).join(', ') || 'no tests parsed'}</Text>
                <VStack align="stretch" spacing={2}>
                  {g.map((s) => {
                    const isPrimary = s.id === primary.id;
                    return (
                      <Flex key={s.id} justify="space-between" align="center" gap={3}
                        p={2} borderRadius="md" bg={isPrimary ? 'green.50' : 'gray.50'} flexWrap="wrap">
                        <HStack spacing={2} minW={0}>
                          <RouterLink to={`/admin/submissions/${s.id}`}>
                            <Badge colorScheme="blue" cursor="pointer">#{s.id}</Badge>
                          </RouterLink>
                          {isPrimary && <Badge colorScheme="green" fontSize="2xs">original (keep)</Badge>}
                          {s.ldx_message_id && <Badge colorScheme="purple" fontSize="2xs">from lab</Badge>}
                          <Text fontSize="xs" color="gray.600">{(s.status || '').replace(/_/g, ' ')}</Text>
                          <Text fontSize="xs" color="gray.400">· {fmtDate(s.submitted_at)}</Text>
                        </HStack>
                        {!isPrimary && (
                          <Button size="xs" colorScheme="purple" variant="outline" leftIcon={<FaExchangeAlt />}
                            onClick={() => openMerge(primary, s)}>
                            Review &amp; merge into #{primary.id}
                          </Button>
                        )}
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            );
          })}
        </VStack>
      )}

      {pair && (
        <MergeSubmissionsModal
          isOpen={!!pair}
          onClose={() => setPair(null)}
          primary={pair.primary}
          duplicate={pair.duplicate}
          onMerged={() => { setPair(null); fetchRows(); }}
        />
      )}
    </Container>
  );
}
